import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import Plot from "react-plotly.js";
import { getCampaignImpressionData } from "../../apis/campaigns.apis";
import { Paper, Typography, Box } from "@mui/material";

// Renamed back to a singular chart, as we're creating one plot.
const LocationImpressionLineChart = ({ campaignId }) => {
  // State for a single plot, matching the data structure
  const [plotData, setPlotData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!campaignId) return;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await getCampaignImpressionData(campaignId);

        if (!Array.isArray(result) || result.length === 0) {
          toast.info("No impression data available to display.");
          setPlotData([]); // Clear any old data
          setLabels([]);
          return;
        }
        
        // ✅ CORRECT DATA TRANSFORMATION FOR A SINGLE CHART
        const formattedLabels = result.map(
          (item) => `${item.location} (${item.height}x${item.width})`
        );
        const xaxis = result.map((item) => item.asset_id);
        const minData = result.map((item) => item.min_impressions);
        const maxData = result.map((item) => item.max_impressions);
        const avgData = result.map((item) => item.avg_impressions);

        setLabels(formattedLabels);
        setPlotData([
          {
            x: xaxis,
            y: minData,
            type: "scatter",
            mode: "lines+markers",
            name: "Min Impressions",
            line: { color: "#ff7f0e" }, // Orange
          },
          {
            x: xaxis,
            y: maxData,
            type: "scatter",
            mode: "lines+markers",
            name: "Max Impressions",
            line: { color: "#2ca02c" }, // Green
          },
          {
            x: xaxis,
            y: avgData,
            type: "scatter",
            mode: "lines+markers",
            name: "Avg Impressions",
            line: { color: "#1f77b4" }, // Blue
          },
        ]);
        toast.success("Impression graph plotted successfully!");
      } catch (error) {
        console.error("Error fetching impression data:", error);
        toast.error("Failed to fetch impression data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [campaignId]);

  if (isLoading) {
    return <Loader open={true} />;
  }
  
  const hasData = plotData && plotData.length > 0;

  return (
    <Paper
      elevation={3}
      sx={{ p: 3, mt: 3, borderRadius: 3, backgroundColor: "#f9f9f9" }}
    >
      <Typography variant="h6" textAlign="center" mb={2} fontWeight="bold">
        Asset-wise Impression Summary
      </Typography>
      {!hasData ? (
        <Box sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="textSecondary">No impression data available to display.</Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", height: 500 }}>
          <Plot
            data={plotData}
            layout={{
              height: 500,
              margin: { t: 40, r: 30, l: 60, b: 150 },
              xaxis: {
                title: "Assets",
                // Use asset_id for positioning and formatted labels for display
                tickvals: plotData[0]?.x, 
                ticktext: labels,
                tickangle: -45,
                tickfont: { size: 10 },
              },
              yaxis: {
                title: "Number of Impressions",
                titlefont: { size: 14 },
              },
              legend: {
                orientation: "h",
                x: 0.5,
                xanchor: 'center',
                y: 1.1,
              },
            }}
            config={{ responsive: true }}
            style={{ width: "100%", height: "100%" }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default LocationImpressionLineChart;